import { DataGridPremium, GridColDef } from '@mui/x-data-grid-premium';
import moment from 'moment';
import { useTranslation } from 'react-i18next';

import { useAllEmployees } from '~/data/employees';
import { Enum, useAllEnums } from '~/data/enums';

import { useSxStyles } from '../common/useStyles';

import { EmplHistoryDetailType } from './data';

export type DataType = {
  data: EmplHistoryDetailType[];
};

type EmplHistoryDetailRow = EmplHistoryDetailType & {
  id: string;
};

export const EmplDetailHistory = (props: DataType) => {
  const { data: emplHistoryData } = props;
  const { t } = useTranslation();
  const sxStyles = useSxStyles();
  const { allEnums } = useAllEnums();
  const { getEmployeeById } = useAllEmployees();

  const getEnumName = (enumValue: Enum[], id: string) => {
    if (id === undefined || id === '') return '';
    const specificEnum = enumValue.find((en) => en.code === id);
    return !specificEnum ? id : specificEnum.name;
  };

  const getEmployeeNameById = (employeeId?: number) => {
    const employee = getEmployeeById(employeeId);
    return employee
      ? `${employee.fEmpLastName}, ${employee.fEmpFirstName}`
      : '';
  };

  const rows: EmplHistoryDetailRow[] = emplHistoryData.map((row, index) => ({
    ...row,
    id: `${row.fEmplDetailStartDate}-${row.fEmplDetailStatus}-${index}`,
  }));

  const columns: GridColDef<EmplHistoryDetailRow>[] = [
    {
      field: 'fEmplDetailStartDate',
      headerName: t('laStartDate'),
      minWidth: 130,
      flex: 1,
      valueGetter: (_value, row) =>
        moment(row.fEmplDetailStartDate).format('DD.MM.YYYY'),
    },
    {
      field: 'fEmplDetailEndDate',
      headerName: t('laEndDate'),
      minWidth: 130,
      flex: 1,
      valueGetter: (_value, row) =>
        moment(row.fEmplDetailEndDate).format('DD.MM.YYYY'),
    },
    {
      field: 'fEmplDetailStatus',
      headerName: t('laEmploymentStatus'),
      minWidth: 160,
      flex: 1,
      valueGetter: (_value, row) =>
        getEnumName(allEnums.EMPLOYMENT_STATUS, row.fEmplDetailStatus),
    },
    {
      field: 'fEmplDetailJobGrade',
      headerName: t('laJobGrade'),
      minWidth: 140,
      flex: 1,
      valueGetter: (_value, row) =>
        getEnumName(allEnums.JOB_GRADE, row.fEmplDetailJobGrade),
    },
    {
      field: 'fEmplDetailTaxNumber',
      headerName: t('laTaxNumber'),
      minWidth: 140,
      flex: 1,
      valueGetter: (_value, row) => row.fEmplDetailTaxNumber ?? '',
    },
    {
      field: 'fEmplDetailVacBalCalcSixDays',
      headerName: t('laVacBalCal'),
      minWidth: 130,
      flex: 1,
      valueGetter: (_value, row) =>
        row.fEmplDetailVacBalCalcSixDays
          ? `6 ${t('laDays')}`
          : `5 ${t('laDays')}`,
    },
    {
      field: 'fEmplDetailPersonnelGroup1',
      headerName: t('fPersonnelGroup1'),
      minWidth: 150,
      flex: 1,
      valueGetter: (_value, row) =>
        row.fEmplDetailPersonnelGroup1
          ? getEnumName(
              allEnums.PERSONNEL_GROUP_1,
              row.fEmplDetailPersonnelGroup1,
            )
          : '',
    },
    {
      field: 'fEmplDetailPersonnelGroup2',
      headerName: t('fPersonnelGroup2'),
      minWidth: 150,
      flex: 1,
      valueGetter: (_value, row) =>
        row.fEmplDetailPersonnelGroup2
          ? getEnumName(
              allEnums.PERSONNEL_GROUP_2,
              row.fEmplDetailPersonnelGroup2,
            )
          : '',
    },
    {
      field: 'fEmplDetailPersonnelGroup3',
      headerName: t('fPersonnelGroup3'),
      minWidth: 150,
      flex: 1,
      valueGetter: (_value, row) =>
        row.fEmplDetailPersonnelGroup3
          ? getEnumName(
              allEnums.PERSONNEL_GROUP_3,
              row.fEmplDetailPersonnelGroup3,
            )
          : '',
    },
    {
      field: 'fEmplDetailPersonnelGroup4',
      headerName: t('fPersonnelGroup4'),
      minWidth: 150,
      flex: 1,
      valueGetter: (_value, row) =>
        row.fEmplDetailPersonnelGroup4
          ? getEnumName(
              allEnums.PERSONNEL_GROUP_4,
              row.fEmplDetailPersonnelGroup4,
            )
          : '',
    },
    {
      field: 'fEmplDetailPersonnelGroup5',
      headerName: t('fPersonnelGroup5'),
      minWidth: 150,
      flex: 1,
      valueGetter: (_value, row) =>
        row.fEmplDetailPersonnelGroup5
          ? getEnumName(
              allEnums.PERSONNEL_GROUP_5,
              row.fEmplDetailPersonnelGroup5,
            )
          : '',
    },
    {
      field: 'fEmplSubEnteredTime',
      headerName: t('laUpdated'),
      minWidth: 160,
      flex: 1,
      valueGetter: (_value, row) =>
        moment(row.fEmplSubEnteredTime).format('DD.MM.YYYY HH:mm'),
    },
    {
      field: 'fEmplSubEnteredUser',
      headerName: t('laUpdatedBy'),
      minWidth: 160,
      flex: 1,
      valueGetter: (_value, row) =>
        getEmployeeNameById(row.fEmplSubEnteredUser),
    },
  ];

  return (
    <DataGridPremium
      rows={rows}
      columns={columns}
      getRowId={(row) => row.id}
      disableColumnMenu={true}
      disableRowSelectionOnClick={true}
      getRowHeight={() => 'auto'}
      initialState={{ density: 'compact' }}
      localeText={{ noRowsLabel: t('laNoHistoryNow') }}
      sx={sxStyles.dataGrid}
    />
  );
};
